"use client";
/* eslint-disable no-undef */
import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

/* 
Portal Wrapper -

Props Accepted:
1. id - the id of the parent container where you'd like your portal to open.
2. dataContainerId (optional) - data-id you'd want to provide to your portal container.
3. className (optional) - className of your portal container.
4. children - content to be rendered inside your portal.
*/

const Portal = ({
  children,
  id,
  dataContainerId = "",
  className = "",
  rootClassName = "",
  rootRef = null,
}) => {
  const { current: portalContainer } = useRef(
    (() => {
      const container =
        typeof window !== "undefined"
          ? window.document?.createElement("div")
          : globalThis.document?.createElement("div");

      if (container) {
        container.dataset.id = dataContainerId;
        container.className = className;
      }
      return container;
    })(),
  );

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const rootEl = rootRef?.current ? rootRef.current : document?.getElementById(id);
    rootEl?.appendChild(portalContainer);
    setMounted(true);
    return () => rootEl?.removeChild(portalContainer);
    // want to run only on mount
  }, []);

  useEffect(() => {
    if (rootClassName?.trim()) {
      const rootEl = document?.getElementById(id);
      const classNameList = rootClassName?.trim().split(/\s+/g) || [];
      rootEl?.classList.add(...classNameList);
      return () => rootEl?.classList.remove(...classNameList);
    }
  }, [rootClassName, id]);

  return mounted && portalContainer ? createPortal(children, portalContainer) : <></>;
};

export default Portal;
